import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { summarizeWithGroq } from '../services/groq';
import styles from './DualSearchComparison.module.css';

const VECTOR_API_URL = "https://vector-search-api-398932329807.us-central1.run.app";

function DualSearchComparison() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [vectorResults, setVectorResults] = useState([]);
  const [textResults, setTextResults] = useState([]);
  const [vectorTime, setVectorTime] = useState(null);
  const [textTime, setTextTime] = useState(null);
  const [error, setError] = useState("");
  const [topK] = useState(10);
  const [useFullSearch, setUseFullSearch] = useState(false);
  const [idealResults, setIdealResults] = useState([]);
  const [finalResults, setFinalResults] = useState([]);
  const [llmLoading, setLlmLoading] = useState(false);
  const [userIntent, setUserIntent] = useState(null);
  
  // Ideal results configuration
  const [idealConfig, setIdealConfig] = useState({
    overlapBoost: 2.0,        // 2x score for overlapping results
    vectorWeight: 0.4,        // 40% weight for vector results
    textWeight: 0.6,          // 60% weight for text results
    maxResults: 10,           // Total results in ideal list
    matchField: 'vendor_name' // Field to detect overlaps
  });

  // LLM filtering configuration
  const [llmConfig, setLlmConfig] = useState({
    enabled: true,
    topN: 5, // Take top 5 from ideal results for LLM filtering
    prompt: `You are an outdoor recreation expert helping users find the most relevant businesses for their search query.

TASK: 
1. First, analyze the user's search intent and explain what type of outdoor activity/service they're looking for
2. Then, evaluate the businesses and return the top 3-5 most relevant ones based on:
   - Direct relevance to the user's search intent
   - Business type and activities offered 
   - Quality indicators (ratings, descriptions)

RESPONSE FORMAT - Return a JSON object with this exact structure:
{
  "userIntent": {
    "activity": "Primary activity/service the user is seeking",
    "reasoning": "Detailed explanation of what the LLM determined the user wants and why"
  },
  "filteredResults": [
    {"vendorName": "...", "metadata": {...}, "sources": [...], "finalScore": 0.95, ...}
  ]
}

If no businesses are truly relevant, return empty filteredResults array but still provide userIntent analysis.`
  });

  const runDualSearch = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setError("");
    setVectorResults([]);
    setTextResults([]);
    setIdealResults([]);
    setFinalResults([]);
    setUserIntent(null);
    setVectorTime(null);
    setTextTime(null);

    try {
      // Run both searches simultaneously
      const [vectorResponse, textResponse] = await Promise.all([
        // Vector search via Google Cloud Run API
        useFullSearch 
          ? fetch(`${VECTOR_API_URL}/search`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: query,
                top_k: topK,
                similarity_threshold: 0.0
              })
            })
          : fetch(`${VECTOR_API_URL}/search-simple?q=${encodeURIComponent(query)}&top_k=${topK}&similarity_threshold=0.0`),
        // Multi-field text search via Supabase with smart limiting to reduce over-fetching
        (() => {
          // Dynamic limit based on query complexity
          const queryTerms = query.toLowerCase().split(' ').filter(term => term.length > 2);
          const queryComplexity = queryTerms.length;
          const hasSpecificTerms = queryTerms.some(term => 
            ['horse', 'climbing', 'kayaking', 'hiking', 'skiing', 'camping', 'biking', 'fishing'].includes(term)
          );
          
          // Smart limiting algorithm:
          // - Simple queries (1-2 terms): 150 records
          // - Medium queries (3-4 terms): 300 records  
          // - Complex queries (5+ terms): 500 records
          // - Activity-specific terms: +100 bonus
          let smartLimit = 150; // Base limit for simple queries
          
          if (queryComplexity >= 3 && queryComplexity <= 4) {
            smartLimit = 300; // Medium complexity
          } else if (queryComplexity >= 5) {
            smartLimit = 500; // High complexity
          }
          
          if (hasSpecificTerms) {
            smartLimit += 100; // Activity-specific bonus
          }
          
          // Cap at reasonable maximum (much lower than original 1000)
          smartLimit = Math.min(smartLimit, 600);
          
          console.log(`Smart limit for query "${query}": ${smartLimit} records (was 1000)`);
          
          return supabase
            .from("vendor_chunks")
            .select("ID, vendor_name, content, category, state")
            .limit(smartLimit);
        })()
      ]);

      // Process vector search results
      let vectorData = { results: [] };
      if (vectorResponse.ok) {
        vectorData = await vectorResponse.json();
        setVectorResults(vectorData.results || []);
        setVectorTime(vectorData.search_time_ms);
      } else {
        throw new Error("Vector search failed");
      }

      // Process text search results
      if (textResponse.error) {
        throw new Error("Text search failed: " + textResponse.error.message);
      } else {
        const textStartTime = performance.now();
        const allData = textResponse.data || [];
        
        // Client-side filtering with field-specific weighting
        const queryLower = query.toLowerCase();
        const queryTerms = queryLower.split(' ').filter(term => term.length > 2);
        
        // Calculate weighted relevance scores for each item
        const scoredData = allData.map(item => {
          let fieldScore = 0;
          
          queryTerms.forEach(term => {
            // Field-specific weights
            if (item.vendor_name && item.vendor_name.toLowerCase().includes(term)) {
              fieldScore += 3.0; // Highest weight for vendor name matches
            }
            if (item.category && item.category.toLowerCase().includes(term)) {
              fieldScore += 2.0; // Medium weight for category matches
            }
            if (item.state && item.state.toLowerCase().includes(term)) {
              fieldScore += 1.0; // Lower weight for state matches
            }
            if (item.content && item.content.toLowerCase().includes(term)) {
              fieldScore += 1.5; // Medium-low weight for content matches
            }
          });
          
          return { ...item, fieldScore };
        });
        
        // Filter to only items with matches and sort by field score
        const filteredData = scoredData
          .filter(item => item.fieldScore > 0)
          .sort((a, b) => b.fieldScore - a.fieldScore)
          .slice(0, topK);
        
        setTextResults(filteredData);
        setTextTime(performance.now() - textStartTime);
        
        // After both searches complete, calculate ideal results and apply LLM filtering
        setTimeout(async () => {
          const idealResultsCalculated = calculateIdealResultsFromData(
            vectorData.results || [], 
            filteredData,
            query
          );
          setIdealResults(idealResultsCalculated);
          
          // Apply LLM filtering if enabled
          if (llmConfig.enabled && idealResultsCalculated.length > 0) {
            const filteredResults = await filterWithLLM(idealResultsCalculated, query);
            setFinalResults(filteredResults);
          } else {
            setFinalResults(idealResultsCalculated);
          }
        }, 100);
      }

    } catch (err) {
      console.error("Dual search error:", err);
      setError(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Find overlapping results by vendor_name
  const getOverlapStatus = (result, isVectorResult) => {
    const targetList = isVectorResult ? textResults : vectorResults;
    const vendorName = isVectorResult 
      ? result.metadata?.vendor_name 
      : result.vendor_name;
    
    return targetList.some(other => {
      const otherName = isVectorResult 
        ? other.vendor_name 
        : other.metadata?.vendor_name;
      return vendorName && otherName && vendorName === otherName;
    });
  };

  // Calculate ideal results combining both searches (using current state)
  const calculateIdealResults = () => {
    return calculateIdealResultsFromData(vectorResults, textResults);
  };

  // Calculate text similarity score with field-specific weighting
  const calculateTextSimilarity = (item, searchQuery) => {
    if (!item || !searchQuery) return 0;
    
    const queryTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 2);
    if (queryTerms.length === 0) return 0;
    
    let totalScore = 0;
    
    queryTerms.forEach(term => {
      let termScore = 0;
      
      // Field-specific scoring with weights
      if (item.vendor_name && item.vendor_name.toLowerCase().includes(term)) {
        const exactMatch = item.vendor_name.toLowerCase() === term ? 2 : 1;
        termScore += exactMatch * 3.0; // 3x weight for vendor_name
      }
      
      if (item.category && item.category.toLowerCase().includes(term)) {
        const exactMatch = item.category.toLowerCase().includes(term) ? 1 : 0.5;
        termScore += exactMatch * 2.0; // 2x weight for category
      }
      
      if (item.state && item.state.toLowerCase().includes(term)) {
        termScore += 1.0; // 1x weight for state
      }
      
      if (item.content && item.content.toLowerCase().includes(term)) {
        // Content analysis with proximity and frequency
        const contentLower = item.content.toLowerCase();
        const exactMatches = (contentLower.match(new RegExp(term, 'g')) || []).length;
        const termIndex = contentLower.indexOf(term);
        const proximityBonus = termIndex >= 0 ? Math.max(0, 1 - (termIndex / contentLower.length)) : 0;
        
        termScore += (exactMatches + proximityBonus * 0.3) * 1.5; // 1.5x weight for content
      }
      
      totalScore += termScore;
    });
    
    // Normalize by query term count
    return Math.min(1, totalScore / (queryTerms.length * 3.0)); // Normalize to 0-1 scale
  };

  // Calculate ideal results from provided data
  const calculateIdealResultsFromData = (vectorData, textData, searchQuery = query) => {
    if (vectorData.length === 0 && textData.length === 0) return [];
    
    const combinedResults = [];
    const seenVendors = new Set();
    
    // Helper function to check overlap with provided data
    const checkOverlap = (vendorName, isVector) => {
      const targetList = isVector ? textData : vectorData;
      return targetList.some(other => {
        const otherName = isVector 
          ? other.vendor_name 
          : other.metadata?.vendor_name;
        return vendorName && otherName && vendorName === otherName;
      });
    };
    
    // Process vector results
    vectorData.forEach((result, index) => {
      const vendorName = result.metadata?.vendor_name;
      if (!vendorName || seenVendors.has(vendorName)) return;
      
      const isOverlap = checkOverlap(vendorName, true);
      const vectorScore = result.similarity_score || 0;
      const rankScore = (vectorData.length - index) / vectorData.length; // Position-based score
      
      let finalScore = vectorScore * idealConfig.vectorWeight * rankScore;
      if (isOverlap) finalScore *= idealConfig.overlapBoost;
      
      combinedResults.push({
        ...result,
        vendorName,
        sources: isOverlap ? ['Vector', 'Text'] : ['Vector'],
        finalScore,
        vectorScore,
        textScore: null,
        isOverlap
      });
      
      seenVendors.add(vendorName);
    });
    
    // Process text results with enhanced scoring
    textData.forEach((result, index) => {
      const vendorName = result.vendor_name;
      if (!vendorName || seenVendors.has(vendorName)) return;
      
      const isOverlap = checkOverlap(vendorName, false);
      
      // Combine multiple scoring methods for text results
      const positionScore = (textData.length - index) / textData.length; // Position-based score
      const fieldWeightScore = result.fieldScore ? result.fieldScore / 10.0 : 0; // Normalize field score to 0-1 range
      const similarityScore = calculateTextSimilarity(result, searchQuery); // Field-weighted similarity
      
      // Calculate a simulated ts_rank based on our similarity algorithm
      // This approximates what PostgreSQL ts_rank would provide
      const simulatedTsRank = Math.min(1.0, similarityScore * 0.4 + fieldWeightScore * 0.3 + positionScore * 0.1);
      
      // Weighted combination of different text scores with field weighting
      const combinedTextScore = (
        positionScore * 0.2 +        // 20% position weight (reduced)
        simulatedTsRank * 0.3 +      // 30% simulated ts_rank weight  
        similarityScore * 0.2 +      // 20% custom similarity weight
        fieldWeightScore * 0.3       // 30% field-specific weight (NEW - vendor_name gets highest)
      );
      
      let finalScore = combinedTextScore * idealConfig.textWeight;
      if (isOverlap) finalScore *= idealConfig.overlapBoost;
      
      combinedResults.push({
        ...result,
        vendorName,
        sources: isOverlap ? ['Vector', 'Text'] : ['Text'],
        finalScore,
        vectorScore: null,
        textScore: combinedTextScore,
        textScoreBreakdown: {
          position: positionScore,
          tsRank: simulatedTsRank,
          similarity: similarityScore,
          combined: combinedTextScore
        },
        // Add simulated rank_score for display
        rank_score: simulatedTsRank,
        isOverlap,
        // Normalize structure to match vector results
        metadata: {
          vendor_name: result.vendor_name,
          category: result.category,
          state: result.state,
          content: result.content
        }
      });
      
      seenVendors.add(vendorName);
    });
    
    // Sort by final score and take top results
    return combinedResults
      .sort((a, b) => b.finalScore - a.finalScore)
      .slice(0, idealConfig.maxResults);
  };

  // LLM filtering function
  const filterWithLLM = async (idealResultsList, searchQuery) => {
    if (!llmConfig.enabled || idealResultsList.length === 0) {
      return idealResultsList;
    }

    try {
      setLlmLoading(true);
      
      // Take top N results for LLM processing
      const topResults = idealResultsList.slice(0, llmConfig.topN);
      
      // Prepare data for LLM
      const businessData = topResults.map(result => ({
        vendorName: result.vendorName,
        category: result.metadata?.category,
        state: result.metadata?.state,
        content: result.metadata?.content,
        sources: result.sources,
        finalScore: result.finalScore,
        isOverlap: result.isOverlap
      }));

      const userMessage = `Search Query: "${searchQuery}"

Businesses to evaluate:
${JSON.stringify(businessData, null, 2)}

Please analyze and return the most relevant businesses for this search query.`;

      const response = await summarizeWithGroq("", {
        messages: [
          { role: "system", content: llmConfig.prompt },
          { role: "user", content: userMessage }
        ],
        temperature: 0.1,
        max_completion_tokens: 2000
      });

      // Parse LLM response
      try {
        // Extract JSON object from response (in case there's extra text)
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          console.warn('No JSON object found in LLM response');
          return [];
        }
        
        const llmResponse = JSON.parse(jsonMatch[0]);
        
        // Store user intent analysis
        if (llmResponse.userIntent) {
          setUserIntent(llmResponse.userIntent);
        }
        
        // Process filtered results
        const filteredResults = llmResponse.filteredResults || [];
        
        // Reconstruct full result objects with original data
        const finalFiltered = filteredResults.map(llmResult => {
          const originalResult = topResults.find(r => r.vendorName === llmResult.vendorName);
          return originalResult ? {
            ...originalResult,
            llmRanked: true,
            llmScore: llmResult.finalScore || originalResult.finalScore
          } : null;
        }).filter(Boolean);

        return finalFiltered;
      } catch (parseError) {
        console.error('Failed to parse LLM response:', parseError);
        console.log('Raw LLM response:', response);
        return [];
      }

    } catch (error) {
      console.error('LLM filtering error:', error);
      return [];
    } finally {
      setLlmLoading(false);
    }
  };

  // Calculate ideal results when vector or text results change
  useEffect(() => {
    if (vectorResults.length > 0 || textResults.length > 0) {
      const idealResultsCalculated = calculateIdealResults();
      setIdealResults(idealResultsCalculated);
    }
  }, [vectorResults, textResults, idealConfig]);

  // Set final results to ideal results (LLM filtering will be triggered manually)
  useEffect(() => {
    setFinalResults(idealResults);
  }, [idealResults]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Dual Search Comparison</h2>
      
      <div className={styles.searchBox}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && query.trim() && runDualSearch()}
          placeholder="Enter search term to compare vector vs text results..."
          className={styles.input}
        />
        <button
          onClick={runDualSearch}
          disabled={loading || !query.trim()}
          className={styles.button}
        >
          {loading ? "Searching..." : "Compare Both Searches"}
        </button>
      </div>

      <div className={styles.toggleContainer}>
        <label className={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={useFullSearch}
            onChange={e => setUseFullSearch(e.target.checked)}
            className={styles.toggleInput}
          />
          <span className={styles.toggleText}>
            Use full /search endpoint (POST with parameters) instead of /search-simple (GET)
          </span>
        </label>
      </div>

      {error && (
        <div className={styles.error}>
          Error: {error}
        </div>
      )}

      {/* User Intent Analysis */}
      {userIntent && (
        <div className={styles.intentAnalysis}>
          <h3 className={styles.intentTitle}>🎯 LLM Intent Analysis</h3>
          <div className={styles.intentContent}>
            <div className={styles.intentActivity}>
              <strong>Detected Activity:</strong> {userIntent.activity}
            </div>
            <div className={styles.intentReasoning}>
              <strong>LLM Reasoning:</strong> {userIntent.reasoning}
            </div>
          </div>
        </div>
      )}

      {(vectorResults.length > 0 || textResults.length > 0) && (
        <div className={styles.resultsContainer}>
          {/* Vector Search Results */}
          <div className={styles.resultColumn}>
            <h3 className={styles.columnTitle}>
              Vector Search Results ({useFullSearch ? '/search' : '/search-simple'})
              {vectorTime !== null && (
                <span className={styles.timing}>({vectorTime.toFixed(1)}ms)</span>
              )}
            </h3>
            <div className={styles.resultsList}>
              {vectorResults.length === 0 ? (
                <div className={styles.noResults}>No vector results found</div>
              ) : (
                vectorResults.map((result, idx) => (
                  <div 
                    key={result.id || idx} 
                    className={`${styles.resultCard} ${getOverlapStatus(result, true) ? styles.overlapping : ''}`}
                  >
                    <div className={styles.cardHeader}>
                      <span className={styles.vendorName}>
                        {result.metadata?.vendor_name || 'Unknown'}
                      </span>
                      {getOverlapStatus(result, true) && (
                        <span className={styles.overlapBadge}>OVERLAP</span>
                      )}
                    </div>
                    <div className={styles.cardBody}>
                      <div><strong>Category:</strong> {result.metadata?.category || 'N/A'}</div>
                      <div><strong>State:</strong> {result.metadata?.state || 'N/A'}</div>
                      <div className={styles.score}>
                        <strong>Similarity:</strong> {result.similarity_score?.toFixed(3) || 'N/A'}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Text Search Results */}
          <div className={styles.resultColumn}>
            <h3 className={styles.columnTitle}>
              Full-Text Search Results
              {textTime !== null && (
                <span className={styles.timing}>({textTime.toFixed(1)}ms)</span>
              )}
            </h3>
            <div className={styles.resultsList}>
              {textResults.length === 0 ? (
                <div className={styles.noResults}>No text results found</div>
              ) : (
                textResults.map((result, idx) => (
                  <div 
                    key={result.ID || idx} 
                    className={`${styles.resultCard} ${getOverlapStatus(result, false) ? styles.overlapping : ''}`}
                  >
                    <div className={styles.cardHeader}>
                      <span className={styles.vendorName}>
                        {result.vendor_name || 'Unknown'}
                      </span>
                      {getOverlapStatus(result, false) && (
                        <span className={styles.overlapBadge}>OVERLAP</span>
                      )}
                    </div>
                    <div className={styles.cardBody}>
                      <div><strong>Category:</strong> {result.category || 'N/A'}</div>
                      <div><strong>State:</strong> {result.state || 'N/A'}</div>
                      <div className={styles.score}>
                        <strong>ts_rank:</strong> {result.rank_score?.toFixed(4) || 'N/A'}
                      </div>
                      <div className={styles.score}>
                        <strong>ID:</strong> {result.ID}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Final LLM-Filtered Results */}
          <div className={styles.resultColumn}>
            <h3 className={styles.columnTitle}>
              LLM-Filtered Results
              <span className={styles.timing}>
                {llmLoading ? '(Filtering...)' : 
                 llmConfig.enabled ? `(Top ${finalResults.length})` : '(LLM Disabled)'}
              </span>
            </h3>
            <div className={styles.resultsList}>
              {llmLoading ? (
                <div className={styles.loading}>🤖 LLM is analyzing results...</div>
              ) : finalResults.length === 0 ? (
                <div className={styles.noResults}>
                  {llmConfig.enabled ? 'None found' : 'No combined results'}
                </div>
              ) : (
                finalResults.map((result, idx) => (
                  <div 
                    key={result.ID || result.id || idx} 
                    className={`${styles.resultCard} ${result.isOverlap ? styles.overlapping : ''}`}
                  >
                    <div className={styles.cardHeader}>
                      <span className={styles.vendorName}>
                        {result.vendorName || 'Unknown'}
                      </span>
                      <div className={styles.sourceBadges}>
                        {result.sources.map(source => (
                          <span 
                            key={source} 
                            className={`${styles.sourceBadge} ${result.isOverlap ? styles.overlapBadge : ''}`}
                          >
                            {source}
                          </span>
                        ))}
                        {result.llmRanked && (
                          <span className={styles.llmBadge}>LLM</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.cardBody}>
                      <div><strong>Category:</strong> {result.metadata?.category || 'N/A'}</div>
                      <div><strong>State:</strong> {result.metadata?.state || 'N/A'}</div>
                      <div className={styles.score}>
                        <strong>Combined Score:</strong> {result.finalScore.toFixed(4)}
                      </div>
                      {result.vectorScore && (
                        <div className={styles.score}>
                          <strong>Vector:</strong> {result.vectorScore.toFixed(3)}
                        </div>
                      )}
                      {result.textScore && (
                        <div className={styles.score}>
                          <strong>Text Combined:</strong> {result.textScore.toFixed(3)}
                        </div>
                      )}
                      {result.textScoreBreakdown && (
                        <div className={styles.score}>
                          <strong>Text Breakdown:</strong> pos:{result.textScoreBreakdown.position.toFixed(2)} | 
                          ts:{result.textScoreBreakdown.tsRank.toFixed(3)} | 
                          sim:{result.textScoreBreakdown.similarity.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DualSearchComparison;